import { FileList } from "./components/FileList";
import { useFileUploadState } from "./hooks/useFileUploadState";
import { UploadedFile } from "./types/UploadedFile.type";
import { DropzoneInput } from "./components/DropZoneInput";
import { UploadStatus } from "./types/UploadStatus.type";
import { FileUploadProps, FileUploadRef } from "./types/FileUpload.types";
import { showToast } from "utils/helpers";
import { DEFAULT_ACCEPTED_FILE_TYPES } from "./constants/acceptedFileTypes";
import { useHasFileUploadContext } from "./hooks/FileUploadContext";
import { convertFileAbleFileToUploadedFile, deleteFileUpload } from "./utils";
import { UploadFile } from "types/storage.types";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { safeRandomUUID } from "newui/utils/general";

const maxFileNameLength = 200;

/**
 * Provides FileUpload capability with in-built drag+drop zone.
 *
 * ⭐ PREFERRED FILE-UPLOAD METHOD. New features must use this component (inside
 * the app-level <FileUploadProvider>) for all uploads. It handles chunked,
 * resumable uploads to `POST /file-upload/store`, per-row lifecycle, validation,
 * and rehydration of existing files via `initialValue`. Do NOT hand-roll bespoke
 * single-shot upload endpoints/components — wire a new `model`/`uploadType` case
 * into FileUploadController + ChunkFileRequest + FILE_UPLOAD_TYPES instead.
 *
 * @param param0
 * @returns
 */
export const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
	(
		{
			allowFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
			children,
			description = null,
			disabled,
			extraData,
			isPreview,
			maxFileSizeMB = 100,
			multiple = false,
			required,
			supportsHardDelete,
			hideFileList = false,
			disableAutoScroll = false,
			onBeforeAccept,
			uploadType,
			initialValue,
			onChange,
		},
		ref
	) => {
		const hasContext = useHasFileUploadContext();
		const bottomRef = useRef<HTMLDivElement | null>(null);

		const mappedInitialFiles = useMemo(
			() =>
				(initialValue ?? []).map((file) => convertFileAbleFileToUploadedFile(file)),
			[initialValue]
		);

		const { files, addFiles, clearFiles, removeFile, replaceFiles, updateUploadedFile } =
			useFileUploadState({
				multiple,
				onChange,
				files: mappedInitialFiles,
			});

		useImperativeHandle(ref, () => ({
			clear: clearFiles,
			removeFile,
			setFiles: replaceFiles,
		}), [clearFiles, removeFile, replaceFiles]);

		// Scroll to bottom when files change. Callers embedded in long pages
		// (e.g. above a table) can opt out via `disableAutoScroll` to avoid a
		// disruptive jump on every upload.
		useEffect(() => {
			if (disableAutoScroll) return;
			if (files.length > 0) {
				bottomRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "end",
				});
			}
		}, [files.length, disableAutoScroll]);

		if (!hasContext) {
			console.error(
				"FileUpload must be used within a FileUploadProvider. " +
				"Wrap your component with <FileUploadProvider>."
			);
			return (
				<div className="rounded-lg border-2 border-red-500 bg-red-50 p-4">
					<p className="font-semibold text-red-700">Configuration Error</p>
					<p className="text-sm text-red-600">
						FileUpload requires FileUploadProvider. Please wrap your component
						with it.
					</p>
				</div>
			);
		}

		const onFileRemoved = async (id: number | string) => {
			// When a file card unmounts (removing the focused delete button),
			// Chakra's Modal focus trap relocates focus, causing the scroll
			// container to jump. Move focus to a stable element BEFORE the
			// card unmounts to prevent the browser-initiated scroll.
			const scrollContainer = document.querySelector(
				'.chakra-modal__content-container'
			);
			if (scrollContainer) {
				// Move focus to the scroll container itself (or modal body)
				// so the browser has no reason to scroll when the button unmounts.
				const modalBody = scrollContainer.querySelector('.chakra-modal__body');
				if (modalBody instanceof HTMLElement) {
					modalBody.focus({ preventScroll: true });
				}
			}

			if (supportsHardDelete) {
				const fileToDelete = files.find((f) => {
					if (typeof id === "string") {
						return f.uploadId === id;
					}
					return f.id === id;
				});

				if (fileToDelete?.id && fileToDelete?.path) {
					// Show deleting state while API call is in progress
					if (fileToDelete.uploadId) {
						updateUploadedFile(fileToDelete.uploadId, {
							...fileToDelete,
							status: UploadStatus.DELETING,
						});
					}

					const uploadFile: UploadFile = {
						id: fileToDelete.id,
						file_name: fileToDelete.name,
						file_path: fileToDelete.path,
						file_size: fileToDelete.size ?? 0,
					};

					const result = await deleteFileUpload(uploadFile, uploadType);

					if (!result.success) {
						// Revert deleting state on failure
						if (fileToDelete.uploadId) {
							updateUploadedFile(fileToDelete.uploadId, {
								...fileToDelete,
								status: UploadStatus.UPLOADED,
							});
						}
						showToast("Failed to delete file. Please try again.", "Error");
						return;
					}
				}
			}

			removeFile(id);
		};

		const handleFilesAccepted = async (rawFiles: File[]) => {
			// Give the consumer a chance to intercept (e.g. for duplicate-filename
			// resolution). They can return a filtered list, or [] to cancel.
			// Consumers may also return the enriched form
			// `{ file, extraData }[]` to attach per-file upload metadata.
			let workingFiles: Array<{ extraData?: Record<string, any>; file: File; }> = rawFiles.map(
				(f) => ({ file: f })
			);
			if (onBeforeAccept) {
				const result = await onBeforeAccept(rawFiles);
				if (result.length === 0) return;
				workingFiles = result.map((entry) =>
					entry instanceof File ? { file: entry } : entry
				);
			}
			const isCorrectFileSize = (file: File) => {
				if (!maxFileSizeMB) {
					return true;
				}

				const isValid =
					maxFileSizeMB && file.size <= maxFileSizeMB * 1024 * 1024;

				if (!isValid) {
					showToast(
						`Maximum file size is ${maxFileSizeMB}MB. File '${file.name}' is larger than this.`,
						"Error"
					);
				}

				return isValid;
			};

			const isCorrectFileNameLength = (fileName: string) => {
				const isValid = fileName.length <= maxFileNameLength;

				if (!isValid) {
					showToast(
						`Maximum file name length is ${maxFileNameLength} characters (including the extension). The file '${fileName}' has ${fileName.length} characters.`,
						"Error"
					);
				}

				return isValid;
			};

			const acceptedFiles: UploadedFile[] = workingFiles.flatMap(({ file, extraData: perFileExtras }) => {
				if (isCorrectFileNameLength(file.name) && isCorrectFileSize(file)) {
					return [
						{
							uploadId: safeRandomUUID(),
							name: file.name,
							size: file.size,
							raw: file,
							status: UploadStatus.IDLE,
							...(perFileExtras ? { extraData: perFileExtras } : {}),
						},
					];
				} else {
					return [];
				}
			});

			if (!multiple && acceptedFiles.length > 1) {
				showToast("Please upload just one file", "Error");
				return;
			}

			addFiles(acceptedFiles);
		};

		const passThruExtraData = { ...extraData, model: uploadType };

		const uploadComplete = (
			originalFile: UploadedFile,
			serverData: UploadedFile
		) => {
			// Merge so the original entry's `name` / `size` survive when the
			// server response is sparse. Strip `raw` and per-file `extraData`
			// — both are upload-trigger inputs, and leaving `raw` set causes
			// `useFileUploadItem`'s auto-start effect to re-upload the file
			// when the row re-mounts under a new key (uploadId flips from the
			// client UUID to the server id string on completion).
			const { raw: _raw, extraData: _extra, ...originalWithoutTriggers } = originalFile;
			updateUploadedFile(originalFile.uploadId, {
				...originalWithoutTriggers,
				...serverData,
				status: UploadStatus.UPLOADED,
			});
		};

		// A disabled FileUpload should behave like preview mode: no dropzone, no delete
		// button on existing files. Callers can still pass `isPreview` explicitly for
		// read-only-but-not-disabled cases. Either flag flips the control to read-only.
		const effectivePreview = isPreview || disabled;

		return (
			<div>
				{!effectivePreview && (
					<DropzoneInput
						children={children}
						disabled={disabled}
						maxFileSizeMB={maxFileSizeMB}
						multiple={multiple}
						accept={allowFileTypes}
						onFilesAccepted={handleFilesAccepted}
						description={description}
					/>
				)}

				{/* Always mount FileList — it drives the per-row upload lifecycle,
				 * so unmounting it would stop chunked uploads from dispatching.
				 * `hideFileList` only toggles visibility. */}
				<div style={hideFileList ? { display: "none" } : undefined}>
					<FileList
						files={files}
						isPreview={effectivePreview}
						extraData={passThruExtraData}
						onRemove={onFileRemoved}
						onUploadComplete={uploadComplete}
					/>
				</div>

				<div ref={bottomRef} />
			</div>
		);
	}
);

FileUpload.displayName = "FileUpload";